import appManage from "../../reducers/app-manager";
import { notify } from "../../components/blocks/Notify";
import { setLog } from "../../helpers/functions";
import { getIpcRenderer } from '../../reducers/ipc-loader.js';
import { onSerialData, onSerialError } from '../../helpers/serial-bus.js';

const RFID_FAIL = "RFID_FAIL";

class moduleRfid {
    /**
     *    let option - массив имеющий следующие параметры
     *    baudRate: 9600,
     *    port: 'COM5', - port
     *
     *    callbackResult - функция куда будет возвращен результат, с аргументом типа String f(String)
     *
     *    после создания объекта, следует использовать команду start()
     */
    constructor(option) {
        this.baudRate = (option.baudRate === undefined) ? 9600 : option.baudRate;
        this.path = (option.port === undefined) ? 'COM4' : option.port;
        this.isHid = option.isHid ? true : false;
        this.isPort = false;
        this.isOpen = false;

        if (!this.isHid) {
            getIpcRenderer().then((ipcRenderer) => {
                if (!ipcRenderer) return;
                ipcRenderer.invoke('init-serial-port', {
                    path: this.path,
                    baudRate: this.baudRate,
                    autoOpen: false,
                    parserConfig: { type: 'regex', regexPattern: "[\\r\\n]+" }
                }).then(res => {
                    if (!res.success) {
                        appManage.isRfidWork(false);
                    }
                }).catch((e) => {
                    setLog('Ошибка init-serial-port', e);
                    appManage.isRfidWork(false);
                });
            }).catch((e) => {
                setLog('Ошибка получения ipcRenderer', e);
                appManage.isRfidWork(false);
            });
        } else {
            appManage.isRfidWork(true);
        }
    }

    open() {        
        return new Promise(async(resolve, reject) => {            
            await new Promise(r => setTimeout(r, 300));            
            if (this.isHid) {
                appManage.isRfidWork(true);
                resolve(true);
                return;
            }

            getIpcRenderer()
                .then((ipcRenderer) => {
                    if (!ipcRenderer) {
                        reject(new Error('ipcRenderer not available'));
                        return;
                    }

                    ipcRenderer.invoke('open-serial-port', { path: this.path })
                        .then(res => {
                            if (!res.success) {
                                appManage.isRfidWork(false);
                                reject(new Error(res.error || 'Failed to open port'));
                                return;
                            }

                            this.isOpen = true;

                            ipcRenderer.send('start-serial-listening', {
                                path: this.path,
                                useParser: true
                            });

                            this.checkStatus().then(status => {                                
                                this.isPort = status;                                
                              
                                appManage.isRfidWork(status);
                                if (!status) {
                                    // устройство не ответило — закрываем порт
                                    this.close().finally(() => resolve(false));
                                } else {
                                    resolve(true);
                                }
                            }).catch((e) => {
                                console.log(e);
                                appManage.isRfidWork(false);
                                reject(e);
                            });
                        })
                        .catch(reject);
                })
                .catch(reject);
        });
    }

    /**
     * Начать выполнение
     * @returns {Promise.Promise}
     */
    start(timeout = 5000) {        
        return new Promise(async (resolve, reject) => {
            if (!this.isOpen) {
                try {
                    await this.open();
                } catch (e) {                    
                    reject(e);
                    return;
                }
            }

            let unsubscribeData = null;
            let unsubscribeError = null;
            let timer = null;
            let settled = false;

            const cleanup = () => {                
                if (unsubscribeData) unsubscribeData();
                if (unsubscribeError) unsubscribeError();
                if (timer) clearTimeout(timer);
            };

            const done = (fn, v) => {                
                if (settled) return;
                settled = true;
                cleanup();
                fn(v);
            };
            unsubscribeData = onSerialData((data) => {                
                if (data.path !== this.path) return;
                if (data.data === 'Err5: Syntax' || data.data === 'ERR') {
                    done(reject, new Error(data.data));
                    return;

                }
                done(resolve, data.data);
            });

            unsubscribeError = onSerialError((err) => {
                if (err.path !== this.path) return;
                done(reject, new Error(err.error));
            });
            
            if(timeout){
                timer = setTimeout(() => {
                    done(reject, new Error('Timeout waiting for RFID data'));
                }, timeout);
            }
        });
    }

    close() {
        return new Promise((resolve, reject) => {
            if (!this.isOpen) {
                resolve();
                return;
            }

            getIpcRenderer()
                .then((ipcRenderer) => {
                    if (!ipcRenderer) {
                        reject(new Error('ipcRenderer not available'));
                        return;
                    }
                    return ipcRenderer.invoke('close-serial-port', this.path);
                })
                .then(() => {
                    this.isOpen = false;
                    this.isPort = false;
                    resolve();
                })
                .catch(reject);
        });
    }

    reset() {
        return new Promise((resolve, reject) => {
            if (!this.isOpen) {
                reject(new Error('Port is not open'));
                return;
            }

            getIpcRenderer()
                .then((ipcRenderer) => {
                    if (!ipcRenderer) {
                        reject(new Error('ipcRenderer not available'));
                        return;
                    }
                    return ipcRenderer.invoke('write-serial-data-drain', {
                        path: this.path,
                        data: '$RESET\r\n',
                        encoding: 'ascii'
                    });
                })
                .then((res) => {
                    if (!res || !res.success) {
                        reject(new Error(res && res.error ? res.error : 'Reset failed'));
                    } else {
                        resolve(res);
                    }
                })
                .catch(reject);
        });
    }

    checkStatus() {
        return new Promise((resolve) => {
            if (this.isHid) {
                // HID-режим — считаем, что устройство работает
                resolve(true);
                return;
            }

            getIpcRenderer()
                .then((ipcRenderer) => {
                    if (!ipcRenderer) {
                        resolve(false);
                        return;
                    }

                    // 1. Проверяем, что порт вообще открыт в main-процессе
                    ipcRenderer.invoke('get-port-info', this.path)
                        .then((info) => {
                            if (!info || !info.success || !info.isOpen) {
                                resolve(false);
                                return;
                            }

                            // 2. Порт открыт. Дополнительно можно проверить,
                            //    что парсер зарегистрирован (у нас regex-парсер).
                            ipcRenderer.invoke('get-parser-config', this.path)
                                .then((parserInfo) => {
                                    if (!parserInfo || !parserInfo.success) {
                                        resolve(false);
                                        return;
                                    }
                                    resolve(true);
                                })
                                .catch(() => resolve(false));
                        })
                        .catch(() => resolve(false));
                })
                .catch(() => resolve(false));
        });
    }
}

export default moduleRfid;